import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useMonth } from '../contexts/MonthContext';
import { LogOut, Settings, ChevronDown, ChevronsUpDown, Zap, List, DollarSign, Upload, Copy, Sun, Moon, BookOpen } from 'lucide-react';
import { cn } from '../lib/utils';
import Logo2 from './logos/Logo2';
import pkg from '../../package.json';
const { version } = pkg;

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const YEARS = Array.from({ length: 16 }, (_, i) => 2020 + i);

const CURRENCIES = [
 { code: 'GBP', symbol: '£', name: 'British Pound' },
 { code: 'USD', symbol: '$', name: 'US Dollar' },
 { code: 'EUR', symbol: '€', name: 'Euro' },
 { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
 { code: 'CAD', symbol: '$', name: 'Canadian Dollar' },
 { code: 'AUD', symbol: '$', name: 'Australian Dollar' },
];

const Layout = () => {
 const { user, signOut } = useAuth();
 const { currentMonth, selectMonth } = useMonth();
 const [showMenu, setShowMenu] = useState(false);
 const [simpleMode, setSimpleMode] = useState(() => {
 const saved = localStorage.getItem('simpleMode');
 if (saved !== null) return saved === 'true';
 return false;
 });
 const [currency, setCurrency] = useState(() => {
 const saved = localStorage.getItem('currency');
 return saved || 'GBP';
 });
 const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
 const [showMonthMenu, setShowMonthMenu] = useState(false);
 const menuRef = useRef(null);
 const monthMenuRef = useRef(null);
 const simpleMonthRef = useRef(null);
 const location = useLocation();
 const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');

 useEffect(() => {
 const root = document.documentElement;
 if (theme === 'dark') {
 root.classList.add('dark');
 } else {
 root.classList.remove('dark');
 }
 localStorage.setItem('theme', theme);
 }, [theme]);
 const isBudgetPage = location.pathname === '/' || location.pathname === '/budget';

 // Parse current month for display
 const [currentYear, currentMonthNum] = currentMonth.split('-').map(Number);
 const displayMonth = `${MONTH_NAMES[currentMonthNum - 1]} ${currentYear}`;
 
 useEffect(() => {
 const handleKeyDown = (e) => {
 if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
 e.preventDefault();
 window.dispatchEvent(new Event('openOnboarding'));
 }
 };
 document.addEventListener('keydown', handleKeyDown);
 return () => document.removeEventListener('keydown', handleKeyDown);
 }, []);

 useEffect(() => {
 const handleClickOutside = (event) => {
 if (menuRef.current && !menuRef.current.contains(event.target)) {
 setShowMenu(false);
 }
 const outsideDesktop = !monthMenuRef.current || !monthMenuRef.current.contains(event.target);
 const outsideSimple = !simpleMonthRef.current || !simpleMonthRef.current.contains(event.target);
 if (outsideDesktop && outsideSimple) {
 setShowMonthMenu(false);
 }
 };

 if (showMenu || showMonthMenu) {
 document.addEventListener('mousedown', handleClickOutside);
 return () => document.removeEventListener('mousedown', handleClickOutside);
 }
 }, [showMenu, showMonthMenu]);

 const toggleSimpleMode = () => {
 const newMode = !simpleMode;
 setSimpleMode(newMode);
 localStorage.setItem('simpleMode', newMode.toString());
 window.dispatchEvent(new Event('simpleModeChange'));
 setShowMenu(false);
 };

 const handleCurrencyChange = (newCurrency) => {
 setCurrency(newCurrency);
 localStorage.setItem('currency', newCurrency);
 window.dispatchEvent(new Event('currencyChange'));
 };

 return (
 <div className="min-h-screen bg-background overflow-x-hidden">
 {/* Simple Header */}
 <div className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border safe-area-header w-full">
 <div className="max-w-4xl mx-auto px-4 py-3 grid grid-cols-[1fr_auto_1fr] items-center w-full">
 {/* Left: Logo always */}
 <div className="flex items-center gap-3">
 <Logo2 className="h-8 w-8" />
 <h1 className="text-xl font-medium text-primary hidden sm:block">DarBudget</h1>
 </div>

 {/* Center: Month picker on budget page */}
 {isBudgetPage ? (
 <div className="relative flex justify-center">
 <div ref={simpleMonthRef} className="sm:hidden">
 <button
 onClick={() => setShowMonthMenu(!showMonthMenu)}
 className="flex items-center gap-1.5 transition-colors"
 >
 <span className="text-sm font-semibold text-foreground whitespace-nowrap">
 {MONTH_NAMES[currentMonthNum - 1].slice(0, 3)} {currentYear}
 </span>
 <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
 </button>
 {showMonthMenu && (
 <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-background border border-border p-4 z-50 min-w-[200px]">
 <div className="flex gap-2 mb-3">
 <div className="relative flex-1">
 <select
 value={currentMonthNum}
 onChange={(e) => selectMonth(currentYear, parseInt(e.target.value))}
 className="w-full appearance-none px-2 py-1.5 pr-6 border border-border bg-background text-sm font-medium text-foreground cursor-pointer"
 >
 {MONTH_NAMES.map((name, idx) => (
 <option key={idx + 1} value={idx + 1}>{name}</option>
 ))}
 </select>
 <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
 </div>
 <div className="relative">
 <select
 value={currentYear}
 onChange={(e) => selectMonth(parseInt(e.target.value), currentMonthNum)}
 className="w-full appearance-none px-2 py-1.5 pr-6 border border-border bg-background text-sm font-medium text-foreground cursor-pointer"
 >
 {YEARS.map((year) => (
 <option key={year} value={year}>{year}</option>
 ))}
 </select>
 <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
 </div>
 </div>
 <button
 onClick={() => { const now = new Date(); selectMonth(now.getFullYear(), now.getMonth() + 1); setShowMonthMenu(false); }}
 className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-accent transition-colors"
 >
 This Month
 </button>
 </div>
 )}
 </div>
 <div ref={monthMenuRef} className="hidden sm:block">
 <button
 onClick={() => setShowMonthMenu(!showMonthMenu)}
 className="flex items-center gap-1.5 px-3 py-1.5 hover:bg-accent transition-colors"
 >
 <span className="text-sm font-semibold text-foreground whitespace-nowrap">{displayMonth}</span>
 <ChevronDown className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", showMonthMenu && "rotate-180")} />
 </button>
 {showMonthMenu && (
 <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-background border border-border p-4 z-50 min-w-[200px]">
 <div className="flex gap-2 mb-3">
 <div className="relative flex-1">
 <select
 value={currentMonthNum}
 onChange={(e) => selectMonth(currentYear, parseInt(e.target.value))}
 className="w-full appearance-none px-2 py-1.5 pr-6 border border-border bg-background text-sm font-medium text-foreground cursor-pointer hover:border-primary transition-colors"
 >
 {MONTH_NAMES.map((name, idx) => (
 <option key={idx + 1} value={idx + 1}>{name}</option>
 ))}
 </select>
 <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
 </div>
 <div className="relative">
 <select
 value={currentYear}
 onChange={(e) => selectMonth(parseInt(e.target.value), currentMonthNum)}
 className="w-full appearance-none px-2 py-1.5 pr-6 border border-border bg-background text-sm font-medium text-foreground cursor-pointer hover:border-primary transition-colors"
 >
 {YEARS.map((year) => (
 <option key={year} value={year}>{year}</option>
 ))}
 </select>
 <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
 </div>
 </div>
 <button
 onClick={() => {
 const now = new Date();
 selectMonth(now.getFullYear(), now.getMonth() + 1);
 setShowMonthMenu(false);
 }}
 className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-accent transition-colors"
 >
 Today / This Month
 </button>
 </div>
 )}
 </div>
 </div>
 ) : <div />}

 <div className="flex justify-end">
 <div className="relative" ref={menuRef}>
 <button
 onClick={() => setShowMenu(!showMenu)}
 className="p-2 transition-colors"
 aria-label="Account menu"
 >
 <Settings className="h-5 w-5 text-muted-foreground" />
 </button>
 {showMenu && (
 <div className="absolute right-0 mt-2 min-w-[240px] w-max max-w-[calc(100vw-1rem)] bg-background shadow-lg border border-border z-50 overflow-hidden">
 <div className="px-4 py-2 border-b border-border">
 <p className="text-sm font-medium text-foreground truncate">{user?.email}</p>
 </div>
 {isBudgetPage && (
 <>
 <button
 onClick={toggleSimpleMode}
 className="sm:hidden w-full flex items-center justify-between px-4 py-2 text-sm text-foreground hover:bg-accent transition-colors"
 >
 <div className="flex items-center gap-3">
 {simpleMode ? <List className="h-4 w-4" /> : <Zap className="h-4 w-4" />}
 <span>{simpleMode ? 'Advanced Mode' : 'Simple Mode'}</span>
 </div>
 </button>
 <button
 onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
 className="w-full flex items-center justify-between px-4 py-2 text-sm text-foreground hover:bg-accent transition-colors"
 >
 <div className="flex items-center gap-3">
 {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
 <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
 </div>
 </button>
 <div className="border-t border-border sm:hidden"></div>
 <button
 onClick={() => {
 window.dispatchEvent(new Event('openImportModal'));
 setShowMenu(false);
 }}
 className="w-full flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-accent transition-colors sm:hidden"
 >
 <Upload className="h-4 w-4" />
 <span>Import CSV</span>
 </button>
 <button
 onClick={() => {
 window.dispatchEvent(new Event('openCopyModal'));
 setShowMenu(false);
 }}
 className="w-full flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-accent transition-colors sm:hidden"
 >
 <Copy className="h-4 w-4" />
 <span>Copy Last Month</span>
 </button>
 <button
 onClick={() => {
 window.dispatchEvent(new Event('openOnboarding'));
 setShowMenu(false);
 }}
 className="w-full flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-accent transition-colors"
 >
 <BookOpen className="h-4 w-4" />
 <span>Run Onboarding</span>
 </button>
 <div className="border-t border-border"></div>
 </>
 )}
 <button
 onClick={() => setShowCurrencyMenu(!showCurrencyMenu)}
 className="w-full flex items-center justify-between px-4 py-2 text-sm text-foreground hover:bg-accent transition-colors"
 >
 <div className="flex items-center gap-3">
 <DollarSign className="h-4 w-4" />
 <span>Currency: {CURRENCIES.find(c => c.code === currency)?.symbol} {currency}</span>
 </div>
 <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", showCurrencyMenu && "rotate-180")} />
 </button>
 {showCurrencyMenu && (
 <div className="border-t border-b border-border divide-y divide-border">
 {CURRENCIES.map((curr) => (
 <button
 key={curr.code}
 onClick={() => {
 handleCurrencyChange(curr.code);
 setShowCurrencyMenu(false);
 }}
 className={cn(
 "w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-3 bl-indicator",
 currency === curr.code
 ? "bl-on text-foreground font-medium bg-accent"
 : "text-muted-foreground hover:bg-accent hover:text-foreground"
 )}
 >
 <span className="font-mono w-5 flex-shrink-0">{curr.symbol}</span>
 <span>{curr.code}</span>
 <span className="text-muted-foreground text-xs">{curr.name}</span>
 </button>
 ))}
 </div>
 )}
 <div className="border-t border-border"></div>
 <button
 onClick={() => {
 signOut();
 setShowMenu(false);
 }}
 className="w-full flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-accent transition-colors"
 >
 <LogOut className="h-4 w-4" />
 Sign out
 </button>
 </div>
 )}
 </div>
 </div>
 </div>
 </div>

 {/* Main content */}
 <div className="pt-14 sm:pt-16 pb-12 safe-area-content w-full">
 <main className="max-w-4xl mx-auto p-3 sm:p-4 w-full">
 <Outlet />
 </main>
 </div>

 {/* Footer - Desktop only */}
 <footer className="hidden sm:block fixed bottom-0 left-0 right-0 bg-background border-t border-border py-2">
 <p className="text-center text-xs text-muted-foreground font-mono">
 © 2026 DarBudget &nbsp;
 <span className="opacity-50">
 v{version}{process.env.REACT_APP_GIT_SHA ? ` · ${process.env.REACT_APP_GIT_SHA.slice(0, 7)}` : ''}
 </span>
 </p>
 </footer>
 </div>
 );
};

export default Layout;

